'use client';

import React, { useState } from 'react';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  GraduationCap,
  Briefcase,
  CheckCircle2,
  TrendingUp,
  Award,
  Clock,
  DollarSign,
  BookOpen,
  Send,
  Loader2,
  Sparkles,
} from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { RestrictedAccessGate } from '@/components/common/RestrictedAccessGate';
import { toast } from 'sonner';

export default function TracerStudyPage() {
  const { isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    nim: '',
    fullName: '',
    graduationYear: '2023',
    employmentStatus: 'employed', // employed, entrepreneur, study, seeking
    waitingMonths: '3',
    currentCompany: '',
    jobTitle: '',
    alignment: 'high', // very_high, high, moderate, low
    salaryRange: '5m_to_10m',
    skillDesignScore: '4',
    skillThermalScore: '4',
    skillManufactureScore: '5',
    skillAutomationScore: '3',
    skillEnglishScore: '3',
    curriculumFeedback: '',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    setTimeout(() => {
      setIsLoading(false);
      setSubmitted(true);
      toast.success('Terima kasih! Data Tracer Study Anda telah berhasil disimpan untuk akreditasi Prodi Teknik Mesin.');
    }, 1200);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background selection:bg-[#D71920] selection:text-white">
      <Navbar />

      <main className="flex-1 pb-20">
        {/* Banner Section */}
        <section className="relative border-b-4 border-[#D71920] bg-[linear-gradient(135deg,#021024_0%,#062347_35%,#004A99_75%,#0066CC_100%)] py-12 lg:py-14 text-white overflow-hidden shadow-lg">
          {/* Crisp Engineering Blueprint Grid */}
          <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:32px_32px] opacity-65 pointer-events-none" />
          {/* Luminous Accents */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-[radial-gradient(circle_at_80%_20%,rgba(215,25,32,0.28),transparent_70%)] pointer-events-none" />
          <div className="absolute -bottom-10 left-1/4 w-80 h-80 bg-[radial-gradient(circle,rgba(0,102,204,0.35),transparent_70%)] pointer-events-none" />
          <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-400/30 bg-blue-500/20 px-3.5 py-1 text-xs font-bold text-blue-200">
                <GraduationCap className="h-3.5 w-3.5 text-red-400" />
                <span>TRACER STUDY RESMI TEKNIK MESIN ITN MALANG</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
                Kuesioner Pelacakan Jejak Alumni (Tracer Study)
              </h1>
              <p className="text-sm sm:text-base text-slate-200 leading-relaxed max-w-3xl">
                Partisipasi Anda sangat menentukan penilaian <strong>Akreditasi Unggul</strong> Program Studi Teknik Mesin ITN Malang dan evaluasi kurikulum berbasis Outcome-Based Education (OBE) agar relevan dengan kebutuhan industri nasional maupun internasional.
              </p>
            </div>

            {/* Quick Stats Highlights */}
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-white/10 backdrop-blur-sm border-t-4 border-t-[#D71920] border-x border-b border-white/10 text-center shadow-sm">
                <span className="text-2xl font-black text-white font-heading">87.4%</span>
                <p className="text-xs text-slate-300 font-medium mt-0.5">Tingkat Keselarasan Kerja</p>
              </div>
              <div className="p-4 rounded-xl bg-white/10 backdrop-blur-sm border-t-4 border-t-blue-400 border-x border-b border-white/10 text-center shadow-sm">
                <span className="text-2xl font-black text-white font-heading">3.2 Bulan</span>
                <p className="text-xs text-slate-300 font-medium mt-0.5">Rata-Rata Waktu Tunggu</p>
              </div>
              <div className="p-4 rounded-xl bg-white/10 backdrop-blur-sm border-t-4 border-t-[#D71920] border-x border-b border-white/10 text-center shadow-sm">
                <span className="text-2xl font-black text-white font-heading">1,420+</span>
                <p className="text-xs text-slate-300 font-medium mt-0.5">Responden Terdata</p>
              </div>
              <div className="p-4 rounded-xl bg-white/10 backdrop-blur-sm border-t-4 border-t-blue-400 border-x border-b border-white/10 text-center shadow-sm">
                <span className="text-2xl font-black text-white font-heading">Unggul</span>
                <p className="text-xs text-slate-300 font-medium mt-0.5">Target Akreditasi LAM Teknik</p>
              </div>
            </div>
          </div>
        </section>

        {/* Option C: Access Control Gate */}
        {!isAuthenticated ? (
          <RestrictedAccessGate
            title="Kuesioner Tracer Study Terkunci"
            subtitle="Untuk menjamin akurasi dan validitas data pelacakan jejak alumni demi Akreditasi Unggul Program Studi Teknik Mesin ITN Malang, formulir Tracer Study hanya dapat diisi oleh alumni resmi yang telah terdaftar dan login."
            featureName="Tracer Study"
            redirectUrl="/tracer-study"
          />
        ) : (
          <section className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 mt-10">
            {submitted ? (
              <Card className="border-[#E2E8F0] bg-card p-8 text-center space-y-6 rounded-2xl shadow-xl">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-500/20 text-emerald-600">
                  <CheckCircle2 className="h-10 w-10" />
                </div>
                <div className="space-y-2">
                  <h2 className="font-heading text-2xl font-black text-[#1F2937]">
                    Data Tracer Study Berhasil Terkirim!
                  </h2>
                  <p className="text-sm text-[#64748B] max-w-md mx-auto leading-relaxed">
                    Terima kasih atas kontribusi nyata Anda untuk almamater tercinta Program Studi Teknik Mesin ITN Malang. Data Anda dijamin kerahasiaannya dan hanya dipergunakan untuk keperluan evaluasi akademik serta akreditasi.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row justify-center gap-3 pt-2">
                  <Button
                    onClick={() => setSubmitted(false)}
                    variant="outline"
                    className="rounded-xl border-[#E2E8F0] text-[#1F2937] hover:bg-slate-50 font-bold"
                  >
                    Isi Kembali / Perbarui
                  </Button>
                  <Button
                    asChild
                    className="rounded-xl bg-[#D71920] hover:bg-[#B8141B] text-white font-bold"
                  >
                    <a href="/alumni">Lihat Direktori Alumni</a>
                  </Button>
                </div>
              </Card>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-8">
              {/* Bagian 1: Identitas Alumni */}
              <Card className="border-[#E2E8F0] bg-card rounded-2xl shadow-sm overflow-hidden">
                <div className="h-1 bg-[#004A99]" />
                <CardHeader className="pb-4 border-b border-[#E2E8F0]">
                  <div className="flex items-center gap-2 text-[#082B52] font-bold text-base">
                    <GraduationCap className="h-5 w-5 text-[#004A99]" />
                    <span>Bagian 1: Identitas Lulusan</span>
                  </div>
                  <CardDescription className="text-xs text-[#64748B]">
                    Pastikan data identitas Anda sesuai dengan ijazah kelulusan ITN Malang.
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-6 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="nim" className="text-xs font-semibold text-[#1F2937]">
                        NIM <span className="text-[#D71920]">*</span>
                      </Label>
                      <Input
                        id="nim"
                        name="nim"
                        placeholder="Contoh: 1921001"
                        value={formData.nim}
                        onChange={handleChange}
                        required
                        className="rounded-xl border-[#E2E8F0] focus-visible:ring-[#004A99]"
                      />
                    </div>

                    <div className="space-y-1.5 sm:col-span-2">
                      <Label htmlFor="fullName" className="text-xs font-semibold text-[#1F2937]">
                        Nama Lengkap Beserta Gelar <span className="text-[#D71920]">*</span>
                      </Label>
                      <Input
                        id="fullName"
                        name="fullName"
                        placeholder="Contoh: Pratama Surya, S.T."
                        value={formData.fullName}
                        onChange={handleChange}
                        required
                        className="rounded-xl border-[#E2E8F0] focus-visible:ring-[#004A99]"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="graduationYear" className="text-xs font-semibold text-[#1F2937]">
                      Tahun Kelulusan <span className="text-[#D71920]">*</span>
                    </Label>
                    <select
                      id="graduationYear"
                      name="graduationYear"
                      value={formData.graduationYear}
                      onChange={handleChange}
                      className="w-full sm:w-1/3 h-10 px-3 py-2 rounded-xl border border-[#E2E8F0] bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#004A99]"
                    >
                      {Array.from({ length: 15 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                        <option key={y} value={y.toString()}>
                          Lulus Tahun {y}
                        </option>
                      ))}
                    </select>
                  </div>
                </CardContent>
              </Card>

              {/* Bagian 2: Status Pekerjaan */}
              <Card className="border-[#E2E8F0] bg-card rounded-2xl shadow-sm overflow-hidden">
                <div className="h-1 bg-[#004A99]" />
                <CardHeader className="pb-4 border-b border-[#E2E8F0]">
                  <div className="flex items-center gap-2 text-[#082B52] font-bold text-base">
                    <Briefcase className="h-5 w-5 text-[#004A99]" />
                    <span>Bagian 2: Status & Riwayat Karir</span>
                  </div>
                  <CardDescription className="text-xs text-[#64748B]">
                    Informasi mengenai situasi kerja, waktu tunggu, dan instansi tempat Anda berkiprah.
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-6 space-y-5">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-[#1F2937]">
                      Status Anda Saat Ini <span className="text-[#D71920]">*</span>
                    </Label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        { id: 'employed', label: 'Bekerja Penuh Waktu / Kontrak di Perusahaan / BUMN' },
                        { id: 'entrepreneur', label: 'Wirausaha / Membuka Usaha Mandiri' },
                        { id: 'study', label: 'Melanjutkan Studi (Magister / Spesialis)' },
                        { id: 'seeking', label: 'Sedang Mencari Pekerjaan / Proses Seleksi' },
                      ].map((item) => (
                        <label
                          key={item.id}
                          className={`flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer transition-colors text-xs font-medium ${
                            formData.employmentStatus === item.id
                              ? 'border-[#004A99] bg-blue-50 text-[#004A99] font-semibold'
                              : 'border-[#E2E8F0] hover:bg-slate-50 text-[#1F2937]'
                          }`}
                        >
                          <input
                            type="radio"
                            name="employmentStatus"
                            value={item.id}
                            checked={formData.employmentStatus === item.id}
                            onChange={handleChange}
                            className="text-[#004A99] focus:ring-[#004A99]"
                          />
                          <span>{item.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="waitingMonths" className="text-xs font-semibold text-[#1F2937]">
                        Waktu Tunggu Mendapat Kerja Pertama (Bulan)
                      </Label>
                      <Input
                        id="waitingMonths"
                        name="waitingMonths"
                        type="number"
                        min="0"
                        max="60"
                        placeholder="Contoh: 3"
                        value={formData.waitingMonths}
                        onChange={handleChange}
                        className="rounded-xl border-[#E2E8F0] focus-visible:ring-[#004A99]"
                      />
                      <p className="text-[10px] text-[#64748B]">
                        Dihitung sejak tanggal wisuda hingga tanda tangan kontrak pertama.
                      </p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="salaryRange" className="text-xs font-semibold text-[#1F2937]">
                        Rentang Rata-Rata Pendapatan Bulanan
                      </Label>
                      <select
                        id="salaryRange"
                        name="salaryRange"
                        value={formData.salaryRange}
                        onChange={handleChange}
                        className="w-full h-10 px-3 py-2 rounded-xl border border-[#E2E8F0] bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[#004A99]"
                      >
                        <option value="under_5m">&lt; Rp 5.000.000</option>
                        <option value="5m_to_10m">Rp 5.000.000 - Rp 10.000.000</option>
                        <option value="10m_to_20m">Rp 10.000.000 - Rp 20.000.000</option>
                        <option value="above_20m">&gt; Rp 20.000.000</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="currentCompany" className="text-xs font-semibold text-[#1F2937]">
                        Nama Perusahaan / Institusi Saat Ini
                      </Label>
                      <Input
                        id="currentCompany"
                        name="currentCompany"
                        placeholder="Contoh: PT Toyota Motor Manufacturing Indonesia"
                        value={formData.currentCompany}
                        onChange={handleChange}
                        className="rounded-xl border-[#E2E8F0] focus-visible:ring-[#004A99]"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="jobTitle" className="text-xs font-semibold text-[#1F2937]">
                        Jabatan / Posisi Kerja
                      </Label>
                      <Input
                        id="jobTitle"
                        name="jobTitle"
                        placeholder="Contoh: Maintenance & Reliability Specialist"
                        value={formData.jobTitle}
                        onChange={handleChange}
                        className="rounded-xl border-[#E2E8F0] focus-visible:ring-[#004A99]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-[#1F2937]">
                      Tingkat Keselarasan Pekerjaan dengan Bidang Ilmu Teknik Mesin <span className="text-[#D71920]">*</span>
                    </Label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        { id: 'very_high', label: 'Sangat Selaras' },
                        { id: 'high', label: 'Selaras' },
                        { id: 'moderate', label: 'Cukup Selaras' },
                        { id: 'low', label: 'Kurang / Tidak Selaras' },
                      ].map((item) => (
                        <label
                          key={item.id}
                          className={`flex items-center justify-center p-3 rounded-xl border text-center cursor-pointer transition-colors text-xs ${
                            formData.alignment === item.id
                              ? 'border-[#004A99] bg-blue-50 text-[#004A99] font-bold'
                              : 'border-[#E2E8F0] hover:bg-slate-50 text-[#64748B]'
                          }`}
                        >
                          <input
                            type="radio"
                            name="alignment"
                            value={item.id}
                            checked={formData.alignment === item.id}
                            onChange={handleChange}
                            className="sr-only"
                          />
                          <span>{item.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bagian 3: Evaluasi Kompetensi & Masukan Kurikulum */}
              <Card className="border-[#E2E8F0] bg-card rounded-2xl shadow-sm overflow-hidden">
                <div className="h-1 bg-[#004A99]" />
                <CardHeader className="pb-4 border-b border-[#E2E8F0]">
                  <div className="flex items-center gap-2 text-[#082B52] font-bold text-base">
                    <BookOpen className="h-5 w-5 text-[#004A99]" />
                    <span>Bagian 3: Evaluasi Kompetensi & Kurikulum</span>
                  </div>
                  <CardDescription className="text-xs text-[#64748B]">
                    Berikan penilaian 1 (Sangat Rendah) hingga 5 (Sangat Tinggi) terhadap kompetensi yang Anda peroleh selama kuliah di Teknik Mesin ITN Malang.
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-6 space-y-5">
                  <div className="space-y-4">
                    {[
                      { key: 'skillDesignScore', label: 'Mechanical Design, CAD/CAM & Simulasi FEA/CFD' },
                      { key: 'skillThermalScore', label: 'Termofluida, Sistem Perpipaan, HVAC & Konversi Energi' },
                      { key: 'skillManufactureScore', label: 'Teknologi Manufaktur, Permesinan, Material & Pengelasan' },
                      { key: 'skillAutomationScore', label: 'Mekatronika, PLC, Sensor & Otomasi Industri' },
                      { key: 'skillEnglishScore', label: 'Komunikasi Bahasa Inggris & Soft Skills Kerja Tim' },
                    ].map((skill) => (
                      <div key={skill.key} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-slate-50 rounded-xl border border-[#E2E8F0]">
                        <span className="text-xs font-medium text-[#1F2937]">{skill.label}</span>
                        <div className="flex items-center gap-2">
                          {['1', '2', '3', '4', '5'].map((score) => (
                            <button
                              type="button"
                              key={score}
                              onClick={() => setFormData((prev) => ({ ...prev, [skill.key]: score }))}
                              className={`h-8 w-8 rounded-lg text-xs font-bold transition-all ${
                                (formData as any)[skill.key] === score
                                  ? 'bg-[#004A99] text-white shadow-sm scale-105'
                                  : 'bg-white border border-[#E2E8F0] text-[#64748B] hover:bg-slate-100'
                              }`}
                            >
                              {score}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="space-y-1.5 pt-2">
                    <Label htmlFor="curriculumFeedback" className="text-xs font-semibold text-[#1F2937]">
                      Saran Peningkatan Kurikulum & Fasilitas Laboratorium Teknik Mesin ITN Malang
                    </Label>
                    <Textarea
                      id="curriculumFeedback"
                      name="curriculumFeedback"
                      rows={4}
                      placeholder="Contoh: Perbanyak praktikum software simulasi industri terbaru (Ansys, SolidWorks), sertifikasi pengelasan/NDT sebelum wisuda, dan pengenalan otomasi industri 4.0..."
                      value={formData.curriculumFeedback}
                      onChange={handleChange}
                      className="rounded-xl text-xs border-[#E2E8F0] focus-visible:ring-[#004A99]"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Submit Button */}
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="rounded-xl bg-[#D71920] hover:bg-[#B8141B] text-white font-bold px-8 h-12 shadow-md shadow-red-500/20"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Menyimpan Data Tracer Study...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Kirim Data Tracer Study
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </section>
        )}
      </main>

      <Footer />
    </div>
  );
}
